import { differenceInMilliseconds, minutesToMilliseconds, parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useInterval } from 'usehooks-ts';

export type IntentionUse = 'set' | 'shared' | 'not-set-active' | 'not-set';

const MINUTES_THRESHOLD = 10;
const POLLING_INTERVAL_MS = 1000;

/**
 * Checks if current time is within threshold minutes of session end
 * Returns false if appointmentEndTime is not available
 */
function isWithinSessionEndThreshold(appointmentEndTime?: string): boolean {
  if (!appointmentEndTime) {
    return false;
  }

  try {
    const endDate = parseISO(appointmentEndTime);
    const now = new Date();
    const msRemaining = differenceInMilliseconds(endDate, now);
    return msRemaining <= minutesToMilliseconds(MINUTES_THRESHOLD);
  } catch {
    return false;
  }
}

function determineIntentionUse(
  intentionCompletedAt: string | undefined,
  intentionPromptSentAt: string | undefined,
  isNearSessionEnd: boolean,
): IntentionUse {
  if (intentionCompletedAt) return 'set';
  if (intentionPromptSentAt) return 'shared';
  if (isNearSessionEnd) return 'not-set-active';
  return 'not-set';
}

/**
 * Hook to determine the intention use state with smart polling
 * Polls for time updates only when necessary
 *
 * @param intentionCompletedAt - Timestamp when intention was completed
 * @param intentionPromptSentAt - Timestamp when prompt was sent to patient
 * @param appointmentEndTime - ISO8601 timestamp of session end
 * @returns The intention use state
 */
export function useIntentionUse(
  intentionCompletedAt: string | undefined,
  intentionPromptSentAt: string | undefined,
  appointmentEndTime: string | undefined,
): IntentionUse {
  const [isNearEnd, setIsNearEnd] = useState<boolean>(() => isWithinSessionEndThreshold(appointmentEndTime));

  // Only enable polling if all intention fields are unset, we have an appointment end time and we're not within 10 minutes of it
  const enablePolling = Boolean(appointmentEndTime && !intentionCompletedAt && !intentionPromptSentAt && !isNearEnd);

  // Poll every second to recalculate when enabled
  useInterval(
    useCallback(() => {
      const nearEnd = isWithinSessionEndThreshold(appointmentEndTime);
      setIsNearEnd(nearEnd);
    }, [appointmentEndTime]),
    enablePolling ? POLLING_INTERVAL_MS : null,
  );

  return useMemo(() => {
    return determineIntentionUse(intentionCompletedAt, intentionPromptSentAt, isNearEnd);
  }, [intentionCompletedAt, intentionPromptSentAt, isNearEnd]);
}
